import React, { useState } from "react";
import { AuditLog, PaginationState } from "../types";
import AuditLogRow from "./AuditLogRow";
import { paginateLogs } from "../utils";
import useGetLanguage from "../../../../utils/useGetLanguage";
import { LanguageText } from "../../../../dictionary/GroupsText";

interface AuditLogTableProps {
  logs: AuditLog[];
  pagination: PaginationState;
  onPaginationChange: (pagination: PaginationState) => void;
  onShowDetails: (log: AuditLog) => void;
  groupId: string;
  ownerID?: string;
}

type SortField = "timestamp" | "user" | "action" | "resource_type";
type SortDirection = "asc" | "desc";

const AuditLogTable: React.FC<AuditLogTableProps> = ({
  logs,
  pagination,
  onPaginationChange,
  onShowDetails,
  groupId,
  ownerID,
}) => {
  const Language: LanguageText = useGetLanguage({
    fileName: "GroupsText",
  });

  const [sortField, setSortField] = useState<SortField>("timestamp");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc"); // Default: newest first

  const totalPages = Math.ceil(pagination.totalItems / pagination.itemsPerPage);

  // Sort logs based on current sort field and direction
  const sortedLogs = React.useMemo(() => {
    // console.log("Sorting logs:", logs);
    const sorted = [...logs].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case "timestamp":
          aValue = new Date(a.timestamp).getTime();
          bValue = new Date(b.timestamp).getTime();
          break;
        case "user":
          aValue = a.user_id.toLowerCase();
          bValue = b.user_id.toLowerCase();
          break;
        case "action":
          aValue = a.action.toLowerCase();
          bValue = b.action.toLowerCase();
          break;
        case "resource_type":
          aValue = a.resource_type.toLowerCase();
          bValue = b.resource_type.toLowerCase();
          break;
        default:
          return 0;
      }

      if (sortDirection === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    // console.log("Sorted logs:", sorted);
    return sorted;
  }, [logs, sortField, sortDirection]);

  const paginatedLogs = paginateLogs(
    sortedLogs,
    pagination.currentPage,
    pagination.itemsPerPage
  );

  const handlePageChange = (page: number) => {
    onPaginationChange({
      ...pagination,
      currentPage: page,
    });
  };

  const handleItemsPerPageChange = (itemsPerPage: number) => {
    onPaginationChange({
      ...pagination,
      itemsPerPage,
      currentPage: 1, // Reset to first page when changing items per page
    });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    // Reset to first page when sorting
    onPaginationChange({
      ...pagination,
      currentPage: 1,
    });
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return (
        <svg
          className="w-4 h-4 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
          />
        </svg>
      );
    }

    if (sortDirection === "asc") {
      return (
        <svg
          className="w-4 h-4 text-blue-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 15l7-7 7 7"
          />
        </svg>
      );
    } else {
      return (
        <svg
          className="w-4 h-4 text-blue-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      );
    }
  };

  return (
    <div
      className="bg-white shadow-sm rounded-lg border border-gray-200 w-full max-w-none min-w-full"
      style={{ width: "100%", minWidth: "100%" }}
    >
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-center">
          {/* <h3 className="text-lg font-medium text-gray-900">Audit Logs</h3> */}
          <div className="flex items-center space-x-4">
            <label className="text-sm text-gray-600">
              {Language?.groupAuditLogs?.show}
            </label>
            <select
              value={pagination.itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {[10, 25, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span className="text-sm text-gray-600">
              {Language?.groupAuditLogs?.entries}
            </span>
          </div>
        </div>
      </div>

      <div
        className="overflow-x-auto w-full min-w-full"
        style={{ width: "100%", minWidth: "100%" }}
      >
        <table
          className="w-full min-w-full divide-y divide-gray-200"
          style={{ width: "100%", minWidth: "100%" }}
        >
          <thead className="bg-gray-50">
            <tr>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                style={{ width: "20%" }}
                onClick={() => handleSort("user")}
              >
                <div className="flex items-center space-x-1">
                  <span>{Language?.groupAuditLogs?.user}</span>
                  {getSortIcon("user")}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                style={{ width: "25%" }}
              >
                {Language?.groupAuditLogs?.email}
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                style={{ width: "15%" }}
                onClick={() => handleSort("action")}
              >
                <div className="flex items-center space-x-1">
                  <span>{Language?.groupAuditLogs?.action}</span>
                  {getSortIcon("action")}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                style={{ width: "15%" }}
                onClick={() => handleSort("resource_type")}
              >
                <div className="flex items-center space-x-1">
                  <span>{Language?.groupAuditLogs?.resource}</span>
                  {getSortIcon("resource_type")}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                style={{ width: "20%" }}
                onClick={() => handleSort("timestamp")}
              >
                <div className="flex items-center space-x-1">
                  <span>{Language?.groupAuditLogs?.date}</span>
                  {getSortIcon("timestamp")}
                </div>
              </th>
              <th
                className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                style={{ width: "5%" }}
              >
                {Language?.groupAuditLogs?.details}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedLogs.length > 0 ? (
              paginatedLogs.map((log) => {
                return (
                  <AuditLogRow
                    key={log.id}
                    log={log}
                    groupId={groupId}
                    ownerID={ownerID}
                    onShowDetails={onShowDetails}
                  />
                );
              })
            ) : (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                  No logs found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">
                {Language?.groupAuditLogs?.showing}{" "}
                {(pagination.currentPage - 1) * pagination.itemsPerPage + 1}{" "}
                {Language?.groupAuditLogs?.to}{" "}
                {Math.min(
                  pagination.currentPage * pagination.itemsPerPage,
                  pagination.totalItems
                )}{" "}
                {Language?.groupAuditLogs?.of} {pagination.totalItems}{" "}
                {Language?.groupAuditLogs?.results}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {Language?.groupAuditLogs?.previous}
              </button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (pagination.currentPage <= 3) {
                  pageNum = i + 1;
                } else if (pagination.currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = pagination.currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-2 text-sm font-medium rounded-md ${
                      pageNum === pagination.currentPage
                        ? "bg-blue-600 text-white"
                        : "text-gray-500 bg-white border border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === totalPages}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {Language?.groupAuditLogs?.next}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogTable;
